/*
 * Impulsa3 - Preferred delivery slot picker for the cart drawer.
 *
 * Persists the shopper's choice as cart attributes via /cart/update.js, so the
 * values survive the drawer re-render (the drawer replaces .drawer__inner with
 * innerHTML on every cart change) and land on the order in the admin.
 *
 * Set IMPULSA3_DISABLE_PAST_SLOTS_ON_SAME_DAY to false to let shoppers pick a
 * slot that has already passed when "Entregar en el día" is on.
 */
const IMPULSA3_DISABLE_PAST_SLOTS_ON_SAME_DAY = true;

if (!customElements.get('impulsa3-delivery-slot')) {
  class Impulsa3DeliverySlot extends HTMLElement {
    connectedCallback() {
      this.slotAttribute = this.dataset.slotAttribute;
      this.sameDayAttribute = this.dataset.samedayAttribute;
      this.sameDayValue = this.dataset.samedayValue || 'Sí';
      this.cutoffHour = parseFloat(this.dataset.samedayCutoffHour || '12');
      this.sameDayEnabled = this.dataset.samedayEnabled !== 'false';
      this.timezone = this.dataset.timezone || '';

      this.radios = Array.from(this.querySelectorAll('.impulsa3-delivery-slot__radio'));
      this.sameDayWrapper = this.querySelector('.impulsa3-delivery-slot__sameday');
      this.sameDayInput = this.querySelector('.impulsa3-delivery-slot__switch-input');
      this.errorMessage = this.querySelector('.impulsa3-delivery-slot__error');

      if (!this.bound) {
        this.radios.forEach((radio) => radio.addEventListener('change', this.onSlotChange.bind(this)));

        if (this.sameDayInput) {
          this.sameDayInput.addEventListener('change', this.onSameDayChange.bind(this));
        }

        this.bound = true;
      }

      this.refreshAvailability();

      // A drawer left open across the cutoff would keep offering same-day.
      this.timer = setInterval(() => this.refreshAvailability(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    /* Current hour in the store's timezone, as a decimal (10:30 -> 10.5). */
    getStoreHour() {
      const now = new Date();

      if (this.timezone) {
        try {
          const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: this.timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }).formatToParts(now);

          const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10);
          const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);

          if (!Number.isNaN(hour) && !Number.isNaN(minute)) return hour + minute / 60;
        } catch (error) {
          // Unsupported timezone: fall through to the device clock.
        }
      }

      return now.getHours() + now.getMinutes() / 60;
    }

    /*
     * Shows the same-day toggle only before the cutoff, greys out slots that
     * can no longer be served today, and clears anything left stale.
     */
    refreshAvailability() {
      const storeHour = this.getStoreHour();
      const beforeCutoff = this.sameDayEnabled && storeHour < this.cutoffHour;

      if (this.sameDayWrapper) this.sameDayWrapper.hidden = !beforeCutoff;

      // The drawer may have been rendered before the cutoff and read after it.
      if (!beforeCutoff && this.sameDayInput && this.sameDayInput.checked) {
        this.sameDayInput.checked = false;
        this.save({ [this.sameDayAttribute]: '' });
      }

      const sameDayOn = Boolean(beforeCutoff && this.sameDayInput && this.sameDayInput.checked);
      let clearedSelection = false;

      this.radios.forEach((radio) => {
        const endHour = parseFloat(radio.dataset.endHour);
        const unavailable = Boolean(
          IMPULSA3_DISABLE_PAST_SLOTS_ON_SAME_DAY && sameDayOn && !Number.isNaN(endHour) && endHour <= storeHour
        );

        radio.disabled = unavailable;

        if (unavailable && radio.checked) {
          radio.checked = false;
          clearedSelection = true;
        }
      });

      if (clearedSelection) this.save({ [this.slotAttribute]: '' });
    }

    onSlotChange(event) {
      this.save({ [this.slotAttribute]: event.target.value });
    }

    onSameDayChange(event) {
      this.save({ [this.sameDayAttribute]: event.target.checked ? this.sameDayValue : '' }).then(() =>
        this.refreshAvailability()
      );
    }

    /* Writes cart attributes and holds the checkout button until it lands. */
    save(attributes) {
      this.setBusy(true);
      if (this.errorMessage) this.errorMessage.hidden = true;

      const cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';

      return fetch(`${cartUpdateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          console.error('[impulsa3-delivery-slot]', error);
          if (this.errorMessage) this.errorMessage.hidden = false;
        })
        .finally(() => this.setBusy(false));
    }

    setBusy(isBusy) {
      this.classList.toggle('impulsa3-delivery-slot--loading', isBusy);

      const checkoutButton = document.getElementById('CartDrawer-Checkout');
      if (!checkoutButton) return;

      // Never re-enable a checkout button that is disabled for its own reason
      // (an empty cart), so only restore what we disabled ourselves.
      if (isBusy) {
        if (checkoutButton.disabled) return;
        checkoutButton.disabled = true;
        checkoutButton.dataset.impulsa3Locked = 'true';
      } else if (checkoutButton.dataset.impulsa3Locked === 'true') {
        checkoutButton.disabled = false;
        delete checkoutButton.dataset.impulsa3Locked;
      }
    }
  }

  customElements.define('impulsa3-delivery-slot', Impulsa3DeliverySlot);
}
